import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Home, MessageSquare, Calendar, Pill, FileText, Bell, UserCircle,
  LayoutDashboard, Menu, BookOpen, TrendingUp, LogOut, MessageCircle, MoreHorizontal, NotebookPen
} from "lucide-react";
import useRealtimeNotifications from "@/hooks/useRealtimeNotifications";
import { useToast } from "@/hooks/use-toast";
import Avatar from "@/components/Avatar";

const patientTabs = [
  { path: "/", icon: Home, label: "Home" },
  { path: "/messages", icon: MessageSquare, label: "Messages" },
  { path: "/medications", icon: Pill, label: "Meds" },
  { path: "/progress", icon: TrendingUp, label: "Progress" },
];

const providerTabs = [
  { path: "/", icon: Home, label: "Home" },
  { path: "/messages", icon: MessageSquare, label: "Messages" },
  { path: "/provider", icon: LayoutDashboard, label: "Patients" },
  { path: "/appointments", icon: Calendar, label: "Schedule" },
  { path: "/resources", icon: BookOpen, label: "Resources" },
];

const patientMenuGroups = [
  {
    label: "Care",
    items: [
      { path: "/appointments", icon: Calendar, label: "Appointments" },
      { path: "/forms", icon: FileText, label: "Forms" },
      { path: "/documents", icon: FileText, label: "Documents" },
    ],
  },
  {
    label: "Wellness",
    items: [
      { path: "/journaling", icon: NotebookPen, label: "Journal" },
      { path: "/resources", icon: BookOpen, label: "Resources" },
      { path: "/forum", icon: MessageCircle, label: "The ADHD Club", forumDot: true },
    ],
  },
  {
    label: "Account",
    items: [
      { path: "/profile", icon: UserCircle, label: "Profile" },
    ],
  },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [unreadCount, setUnreadCount] = useState(0);
  const [forumUnread, setForumUnread] = useState(0);
  const [bellGlow, setBellGlow] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const prevUnreadRef = useRef(0);
  const isProvider = user?.role === "admin";

  // Trigger bell glow when unread count increases
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setBellGlow(true);
      const t = setTimeout(() => setBellGlow(false), 1800);
      prevUnreadRef.current = unreadCount;
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // Real-time WebSocket notifications
  const handleNotification = useCallback((data) => {
    if (data?.type === "reaction" || data?.type === "typing" || data?.type === "thread_refresh") {
      window.dispatchEvent(new CustomEvent("realtime:message_event", { detail: data }));
      return;
    }
    if (data?.type === "safety_alert") {
      window.dispatchEvent(new CustomEvent("realtime:safety_alert", { detail: data }));
      setUnreadCount((c) => c + 1);
      toast({
        title: data.title || "Safety alert",
        description: data.body || "A patient may need follow-up.",
        duration: 10000,
        variant: "destructive",
      });
      return;
    }
    setUnreadCount((c) => c + 1);
    toast({
      title: data.title || "New Notification",
      description: data.body || "",
      duration: 5000,
    });
  }, [toast]);

  useRealtimeNotifications(user?.id, handleNotification);

  // Check onboarding status for non-admin users
  useEffect(() => {
    if (!user || user.role === "admin") return;
    if (!user.onboarded && location.pathname !== "/onboarding") {
      API.get("/intakeq/mapping-status").then(({ data }) => {
        if (data.status !== "verified") {
          navigate("/onboarding");
        }
      }).catch(() => {});
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => API.get("/notifications/unread").then(({ data }) => {
      const list = data || [];
      setUnreadCount(list.length);
      setForumUnread(list.filter(n => n.type === "forum_reply").length);
    }).catch(() => {});
    fetchUnread();
    const handler = () => fetchUnread();
    window.addEventListener("notifications:refresh", handler);
    return () => window.removeEventListener("notifications:refresh", handler);
  }, [user]);

  // Clear badge when visiting notifications page
  useEffect(() => {
    if (location.pathname === "/notifications" && unreadCount > 0) {
      API.post("/notifications/mark-all-read").then(() => setUnreadCount(0)).catch(() => {});
    }
  }, [location.pathname, unreadCount]);

  const closeMore = () => setMoreOpen(false);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-header text-header-foreground px-4 py-2.5 flex items-center justify-between sticky top-0 z-50 shadow-lg" data-testid="app-header">
        <Link to="/" className="flex items-center group" data-testid="header-home-link" aria-label="Go to home">
          <img
            src="/brand/logo-reverse.png"
            alt="Elevated Psychiatry"
            className="h-10 w-auto transition-transform duration-200 group-hover:scale-[1.03] group-active:scale-95"
            data-testid="app-logo-header"
          />
        </Link>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="nav-menu-button">
                <Menu className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem asChild>
                <Link to="/messages" className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Messages</Link>
              </DropdownMenuItem>
              {!isProvider && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/medications" className="flex items-center gap-2"><Pill className="w-4 h-4" /> Meds</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/progress" className="flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Progress</Link>
                  </DropdownMenuItem>
                  {patientMenuGroups.map((group) => (
                    <div key={group.label}>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground py-1">{group.label}</DropdownMenuLabel>
                      {group.items.map((item) => (
                        <DropdownMenuItem asChild key={item.path}>
                          <Link to={item.path} className="flex items-center gap-2 relative" data-testid={`nav-menu-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                            <item.icon className="w-4 h-4" /> {item.label}
                            {item.forumDot && forumUnread > 0 && (
                              <span className="ml-auto w-2 h-2 rounded-full bg-secondary animate-pulse" aria-label={`${forumUnread} new forum replies`} data-testid="nav-forum-dot" />
                            )}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ))}
                </>
              )}
              {isProvider && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/appointments" className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Appointments</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/provider" className="flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> Provider Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/resources" className="flex items-center gap-2" data-testid="nav-resources-menu-item"><BookOpen className="w-4 h-4" /> Resources</Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-destructive" data-testid="nav-logout">
                <LogOut className="w-4 h-4" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link
            to="/notifications"
            className={`relative p-2 rounded-full transition-all duration-300 ${
              bellGlow
                ? "bg-white/20 ring-2 ring-secondary ring-offset-2 ring-offset-header shadow-[0_0_18px_rgba(230,136,119,0.55)]"
                : "hover:bg-white/10"
            }`}
            data-testid="nav-notifications"
          >
            <Bell className={`w-5 h-5 ${bellGlow ? "animate-[wiggle_0.6s_ease-in-out_2]" : ""}`} />
            {unreadCount > 0 && (
              <span className={`absolute -top-0.5 -right-0.5 w-5 h-5 bg-secondary text-secondary-foreground text-[10px] font-bold rounded-full flex items-center justify-center ${bellGlow ? "animate-ping-once" : ""}`}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Link>
          <Link to="/profile" className="p-1 rounded-full hover:bg-white/10 transition-colors ml-1" data-testid="nav-profile" aria-label="Profile">
            {user?.avatar_url ? (
              <Avatar src={user.avatar_url} size="sm" className="ring-white/40" />
            ) : user ? (
              <Avatar name={user.full_name} email={user.email} size="sm" className="ring-white/40" />
            ) : (
              <UserCircle className="w-5 h-5" />
            )}
          </Link>
        </div>
      </header>

      <main className="flex-1 pb-20 md:pb-4">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border md:hidden z-50 safe-area-bottom" data-testid="bottom-nav">
        <div className="flex items-center justify-around py-1">
          {(isProvider ? providerTabs : patientTabs).map((item) => {
            const isActive = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-tab-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-0.5 py-2 px-3 rounded-xl transition-all ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : ""}`} />
                <span className={`text-[10px] ${isActive ? "font-semibold" : "font-medium"}`}>{item.label}</span>
              </Link>
            );
          })}
          {!isProvider && (
            <button
              onClick={() => setMoreOpen(true)}
              data-testid="nav-tab-more"
              className="flex flex-col items-center gap-0.5 py-2 px-3 rounded-xl text-muted-foreground hover:text-foreground transition-all"
            >
              <div className="relative">
                <MoreHorizontal className="w-5 h-5" />
                {forumUnread > 0 && (
                  <span className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full bg-secondary ring-2 ring-card animate-pulse" data-testid="nav-tab-more-forum-dot" />
                )}
              </div>
              <span className="text-[10px] font-medium">More</span>
            </button>
          )}
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto pb-8" data-testid="more-sheet">
          <SheetHeader className="text-left pb-2">
            <SheetTitle className="font-display text-lg">More</SheetTitle>
          </SheetHeader>
          <div className="space-y-5">
            {patientMenuGroups.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{group.label}</p>
                <div className="grid grid-cols-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMore}
                      data-testid={`more-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-accent transition-colors text-sm font-medium"
                    >
                      <item.icon className="w-4 h-4 text-muted-foreground" />
                      {item.label}
                      {item.forumDot && forumUnread > 0 && (
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded">{forumUnread} new</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            <button
              onClick={() => { closeMore(); logout(); }}
              className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-accent transition-colors text-sm font-medium text-destructive w-full"
              data-testid="more-sign-out"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
